import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Brain, ShieldCheck, LogOut, RefreshCw, Send, Loader2, Mail, Users, Zap,
  Clock, Sparkles, ExternalLink, CheckCircle2, AlertCircle, Trash2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_TOKEN_KEY = 'neurowave_admin_token';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const StatCard = ({ label, value, sublabel, accent = 'purple' }) => {
  const accents = {
    purple: 'from-purple-500/15 to-purple-500/5 border-purple-500/20 text-purple-300',
    cyan: 'from-cyan-500/15 to-cyan-500/5 border-cyan-500/20 text-cyan-300',
    emerald: 'from-emerald-500/15 to-emerald-500/5 border-emerald-500/20 text-emerald-300',
    amber: 'from-amber-500/15 to-amber-500/5 border-amber-500/20 text-amber-300',
    pink: 'from-pink-500/15 to-pink-500/5 border-pink-500/20 text-pink-300',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${accents[accent]} p-4`}>
      <div className="text-xs text-white/45 uppercase tracking-wider">{label}</div>
      <div className="text-3xl font-semibold text-white mt-1">{value?.toLocaleString?.() ?? value}</div>
      {sublabel && <div className="text-xs text-white/40 mt-1">{sublabel}</div>}
    </div>
  );
};

const AdminPanel = () => {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(LS_TOKEN_KEY) || ''; } catch { return ''; }
  });
  const [tokenInput, setTokenInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [recent, setRecent] = useState([]);
  const [actionLog, setActionLog] = useState([]);
  const [loading, setLoading] = useState({ metrics: false, users: false, reseed: false, digest: false });
  const [authError, setAuthError] = useState('');

  const appendLog = (kind, text) => {
    setActionLog((log) => [{ ts: new Date().toLocaleTimeString(), kind, text }, ...log].slice(0, 10));
  };

  const headers = useCallback(() => ({ 'X-Admin-Token': token, 'Content-Type': 'application/json' }), [token]);

  const loadMetrics = useCallback(async () => {
    setLoading((l) => ({ ...l, metrics: true }));
    try {
      const r = await fetch(`${API}/admin/metrics`, { headers: headers() });
      if (r.status === 401) { setAuthed(false); setAuthError('Token rejected by server'); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMetrics(await r.json());
      setAuthed(true);
    } catch (e) {
      appendLog('error', `Metrics failed: ${e.message}`);
    } finally {
      setLoading((l) => ({ ...l, metrics: false }));
    }
  }, [headers]);

  const loadRecent = useCallback(async () => {
    setLoading((l) => ({ ...l, users: true }));
    try {
      const r = await fetch(`${API}/admin/users/recent?limit=25`, { headers: headers() });
      if (!r.ok) return;
      const data = await r.json();
      setRecent(data.users || []);
    } finally {
      setLoading((l) => ({ ...l, users: false }));
    }
  }, [headers]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      await loadMetrics();
      await loadRecent();
    })();
  }, [token, loadMetrics, loadRecent]);

  const submitToken = (e) => {
    e.preventDefault();
    setAuthError('');
    if (!tokenInput.trim()) return;
    try { localStorage.setItem(LS_TOKEN_KEY, tokenInput.trim()); } catch { /* ignore */ }
    setToken(tokenInput.trim());
    setTokenInput('');
  };

  const logout = () => {
    try { localStorage.removeItem(LS_TOKEN_KEY); } catch { /* ignore */ }
    setToken('');
    setAuthed(false);
    setMetrics(null);
    setRecent([]);
  };

  const reseedPresets = async () => {
    if (!window.confirm('Delete all built-in presets and reseed from the curated catalog?\nUser presets are untouched.')) return;
    setLoading((l) => ({ ...l, reseed: true }));
    try {
      const r = await fetch(`${API}/admin/reseed-presets`, { method: 'POST', headers: headers() });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Reseed failed');
      appendLog('ok', `Reseeded ${data.seeded} presets (${data.free} free + ${data.premium} premium)`);
      await loadMetrics();
    } catch (e) {
      appendLog('error', e.message);
    } finally {
      setLoading((l) => ({ ...l, reseed: false }));
    }
  };

  const sendDigest = async (dryRun) => {
    const period = window.prompt('Period? "month" or "week"', 'month');
    if (!period || !['month', 'week'].includes(period)) return;
    if (!dryRun && !window.confirm(`Send a "${period}" digest to ALL verified, opted-in users? This is live email delivery.`)) return;
    setLoading((l) => ({ ...l, digest: true }));
    try {
      const r = await fetch(`${API}/digest/send-to-all?period=${period}&dry_run=${dryRun}`, {
        method: 'POST', headers: headers(),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Digest failed');
      appendLog(dryRun ? 'ok' : 'ok',
        `${dryRun ? 'Dry run' : 'Live send'}: ${data.sent}/${data.recipients} sent (${data.failed} failed), period: ${data.period}`);
    } catch (e) {
      appendLog('error', e.message);
    } finally {
      setLoading((l) => ({ ...l, digest: false }));
    }
  };

  const openDigestPreview = () => {
    const url = `${API}/digest/preview`;
    // Open the preview as a new tab using the stashed token via a programmatic fetch → blob → URL
    fetch(url, { method: 'POST', headers: headers() })
      .then((r) => r.text())
      .then((html) => {
        const blob = new Blob([html], { type: 'text/html' });
        const href = URL.createObjectURL(blob);
        window.open(href, '_blank');
      })
      .catch((e) => appendLog('error', `Preview failed: ${e.message}`));
  };

  // ─── Customer rescue actions ────────────────────────────
  const [rescueEmail, setRescueEmail] = useState('');
  const [rescueReason, setRescueReason] = useState('Stripe webhook failed to upgrade');
  const [rescueStripeSession, setRescueStripeSession] = useState('');

  const upgradeUser = async () => {
    const email = rescueEmail.trim().toLowerCase();
    if (!email) return;
    if (!window.confirm(`Grant premium lifetime to ${email}?`)) return;
    try {
      const r = await fetch(`${API}/admin/upgrade-user`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ email, reason: rescueReason, stripe_session_id: rescueStripeSession || undefined }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Upgrade failed');
      appendLog('ok', `Upgraded ${data.email} → premium lifetime (user_id ${data.user_id})`);
      await loadMetrics();
      await loadRecent();
    } catch (e) {
      appendLog('error', `Upgrade failed: ${e.message}`);
    }
  };

  const triggerPasswordReset = async () => {
    const email = rescueEmail.trim().toLowerCase();
    if (!email) return;
    try {
      const r = await fetch(`${API}/admin/trigger-password-reset`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ email }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Reset trigger failed');
      appendLog(data.success ? 'ok' : 'error',
        data.success
          ? `Password reset email re-sent to ${data.email}`
          : `Reset API call succeeded but Resend reported failure — check Resend dashboard`);
    } catch (e) {
      appendLog('error', `Reset trigger failed: ${e.message}`);
    }
  };

  // ─── Hard-delete user (type-to-confirm) ─────────────────
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const deleteUser = async () => {
    const email = deleteEmail.trim().toLowerCase();
    if (!email) return;
    if (deleteConfirm !== 'DELETE') {
      appendLog('error', 'Type DELETE (uppercase) to confirm');
      return;
    }
    if (!window.confirm(`Permanently delete ${email} and ALL their data? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API}/admin/delete-user`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ email, confirm: 'DELETE' }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Delete failed');
      const summary = Object.entries(data.deleted_counts || {})
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${k}:${n}`)
        .join(', ') || 'no rows';
      appendLog('ok', `Deleted ${data.email} (${summary})`);
      setDeleteEmail('');
      setDeleteConfirm('');
      await loadMetrics();
      await loadRecent();
    } catch (e) {
      appendLog('error', `Delete failed: ${e.message}`);
    }
  };

  // ─── Token gate ─────────────────────────────────────────
  if (!token || !authed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-[#0e0a1f] to-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white/[0.04] border border-white/10 rounded-2xl p-7">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-white text-lg font-semibold">Admin</h1>
              <p className="text-white/40 text-xs">NeuroWave One</p>
            </div>
          </div>
          <form onSubmit={submitToken} className="space-y-3">
            <input
              type="password"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="Admin token"
              autoFocus
              autoComplete="off"
              className="w-full bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-purple-500/50"
              data-testid="admin-token-input"
            />
            {authError && <p className="text-red-400 text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {authError}</p>}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white py-5 rounded-xl font-semibold text-sm"
              data-testid="admin-token-submit"
            >
              Enter Dashboard
            </Button>
          </form>
          <p className="text-white/35 text-xs mt-4 leading-relaxed">
            Token value is your backend <code className="text-white/60">DIGEST_ADMIN_TOKEN</code> env var.
            Stored only in your browser's localStorage.
          </p>
          <Link to="/" className="block mt-4 text-white/40 hover:text-white/70 text-xs">← Back to site</Link>
        </div>
      </div>
    );
  }

  // ─── Authed dashboard ───────────────────────────────────
  const u = metrics?.users || {};
  const p = metrics?.presets || {};
  const s = metrics?.sessions || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-[#0e0a1f] to-slate-950 text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-white/5 bg-black/20 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center">
              <Brain className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm font-semibold">NeuroWave Admin</h1>
              <p className="text-[10px] text-white/40">{metrics?.generated_at ? `Generated ${fmtDate(metrics.generated_at)}` : 'Loading…'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={loadMetrics} disabled={loading.metrics} variant="ghost" className="text-white/60 hover:text-white text-xs h-8" data-testid="admin-refresh-btn">
              {loading.metrics ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Refresh
            </Button>
            <Button onClick={logout} variant="ghost" className="text-white/40 hover:text-white/80 text-xs h-8" data-testid="admin-logout-btn">
              <LogOut className="w-3.5 h-3.5 mr-1" /> Exit
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-7 space-y-8">
        {/* Metrics grid */}
        <section>
          <h2 className="text-xs uppercase tracking-wider text-white/40 mb-3 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Users</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total users" value={u.total ?? 0} accent="purple" />
            <StatCard label="Verified" value={u.verified ?? 0} sublabel={u.total ? `${Math.round((u.verified / u.total) * 100)}% of all` : ''} accent="cyan" />
            <StatCard label="Premium" value={u.premium ?? 0} accent="emerald" />
            <StatCard label="Digest opt-outs" value={u.digest_opt_out ?? 0} accent="amber" />
            <StatCard label="Signups 24h" value={u.signups_24h ?? 0} accent="pink" />
            <StatCard label="Signups 7 days" value={u.signups_7d ?? 0} accent="pink" />
            <StatCard label="Signups 30 days" value={u.signups_30d ?? 0} accent="pink" />
            <StatCard label="Sessions 7 days" value={s.last_7d ?? 0} accent="cyan" />
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Ops */}
          <div className="lg:col-span-2 bg-white/[0.03] border border-white/5 rounded-2xl p-5">
            <h2 className="text-xs uppercase tracking-wider text-white/40 mb-3 flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> Operations</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={reseedPresets}
                disabled={loading.reseed}
                className="flex items-start gap-3 p-3 rounded-xl bg-emerald-500/8 hover:bg-emerald-500/15 border border-emerald-500/20 transition text-left disabled:opacity-40"
                data-testid="admin-action-reseed"
              >
                <Sparkles className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm text-white font-medium">Reseed preset catalog</div>
                  <div className="text-xs text-white/45 mt-0.5">Drop built-ins & re-insert from the data module. Safe — user presets untouched. Currently {p.builtin ?? '…'} built-in.</div>
                </div>
              </button>
              <button
                onClick={openDigestPreview}
                className="flex items-start gap-3 p-3 rounded-xl bg-cyan-500/8 hover:bg-cyan-500/15 border border-cyan-500/20 transition text-left"
                data-testid="admin-action-digest-preview"
              >
                <Mail className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm text-white font-medium">Preview digest email</div>
                  <div className="text-xs text-white/45 mt-0.5">Opens a rendered preview in a new tab. Nothing sent.</div>
                </div>
              </button>
              <button
                onClick={() => sendDigest(true)}
                disabled={loading.digest}
                className="flex items-start gap-3 p-3 rounded-xl bg-purple-500/8 hover:bg-purple-500/15 border border-purple-500/20 transition text-left disabled:opacity-40"
                data-testid="admin-action-digest-dry"
              >
                <Send className="w-4 h-4 text-purple-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm text-white font-medium">Send digest (dry run)</div>
                  <div className="text-xs text-white/45 mt-0.5">Count recipients without sending. Always do this first.</div>
                </div>
              </button>
              <button
                onClick={() => sendDigest(false)}
                disabled={loading.digest}
                className="flex items-start gap-3 p-3 rounded-xl bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/30 transition text-left disabled:opacity-40"
                data-testid="admin-action-digest-live"
              >
                <Send className="w-4 h-4 text-pink-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm text-white font-medium">Send digest (LIVE)</div>
                  <div className="text-xs text-white/45 mt-0.5">Hits Resend. Will send to all opted-in verified users.</div>
                </div>
              </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
              <a href="https://sentry.io" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-white/50 hover:text-white/80 underline-offset-2 hover:underline">
                Open Sentry <ExternalLink className="w-3 h-3" />
              </a>
              <span className="text-white/20">•</span>
              <a href="https://resend.com/emails" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-white/50 hover:text-white/80 underline-offset-2 hover:underline">
                Open Resend <ExternalLink className="w-3 h-3" />
              </a>
              <span className="text-white/20">•</span>
              <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-white/50 hover:text-white/80 underline-offset-2 hover:underline">
                Open Stripe <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Top presets last 7 days */}
          <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
            <h2 className="text-xs uppercase tracking-wider text-white/40 mb-3 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Top presets (7d)</h2>
            <ul className="space-y-2">
              {(metrics?.top_presets_7d || []).map((tp, i) => (
                <li key={tp.name + i} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-white/80 truncate">{i + 1}. {tp.name}</span>
                  <span className="text-white/50 tabular-nums shrink-0">{tp.count}</span>
                </li>
              ))}
              {(!metrics?.top_presets_7d || metrics.top_presets_7d.length === 0) && (
                <li className="text-white/35 text-xs">No sessions in the last 7 days yet.</li>
              )}
            </ul>
          </div>
        </section>

        {/* Action log */}
        {actionLog.length > 0 && (
          <section className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
            <h2 className="text-xs uppercase tracking-wider text-white/40 mb-3">Recent admin actions</h2>
            <ul className="space-y-1.5 text-xs font-mono">
              {actionLog.map((l, i) => (
                <li key={i} className={l.kind === 'error' ? 'text-red-300' : 'text-emerald-300'}>
                  {l.kind === 'error' ? <AlertCircle className="w-3 h-3 inline mr-1" /> : <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                  [{l.ts}] {l.text}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Customer rescue — manual upgrade / resend reset email */}
        <section className="bg-rose-500/[0.04] border border-rose-500/20 rounded-2xl p-5" data-testid="admin-rescue-section">
          <h2 className="text-xs uppercase tracking-wider text-rose-300 mb-3 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> Customer rescue
          </h2>
          <p className="text-white/50 text-xs mb-4 leading-relaxed">
            Use when a payment succeeded in Stripe but the user is stuck on trial (missing webhook, mismatched checkout email, email delivery failure, etc). Every action writes an audit record to <code className="text-white/70">payment_transactions</code>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            <input
              type="email"
              placeholder="customer@email.com"
              value={rescueEmail}
              onChange={(e) => setRescueEmail(e.target.value)}
              className="md:col-span-1 bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-500/40"
              data-testid="rescue-email-input"
            />
            <input
              type="text"
              placeholder="Audit reason (e.g. 'Stripe webhook missed, refund processed')"
              value={rescueReason}
              onChange={(e) => setRescueReason(e.target.value)}
              className="md:col-span-1 bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-500/40"
              data-testid="rescue-reason-input"
            />
            <input
              type="text"
              placeholder="Stripe session id (cs_... — optional)"
              value={rescueStripeSession}
              onChange={(e) => setRescueStripeSession(e.target.value)}
              className="md:col-span-1 bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-rose-500/40"
              data-testid="rescue-stripe-session-input"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={upgradeUser}
              disabled={!rescueEmail}
              className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white rounded-lg px-4 py-2 text-sm"
              data-testid="rescue-upgrade-btn"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Grant premium lifetime
            </Button>
            <Button
              onClick={triggerPasswordReset}
              disabled={!rescueEmail}
              variant="outline"
              className="bg-white/5 border-white/15 hover:bg-white/10 text-white rounded-lg px-4 py-2 text-sm"
              data-testid="rescue-reset-btn"
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" />
              Re-send password reset
            </Button>
          </div>
        </section>

        {/* Danger zone — hard delete user with type-to-confirm */}
        <section className="bg-red-500/[0.05] border border-red-500/30 rounded-2xl p-5" data-testid="admin-delete-section">
          <h2 className="text-xs uppercase tracking-wider text-red-300 mb-3 flex items-center gap-1">
            <Trash2 className="w-3.5 h-3.5" /> Danger zone — delete user
          </h2>
          <p className="text-white/50 text-xs mb-4 leading-relaxed">
            Hard-deletes the user and cascades across their sessions, custom presets, subscription, payment records, referrals, and achievements. Built-in presets stay untouched. An entry is written to <code className="text-white/70">admin_audit</code>. This is <strong className="text-red-300">permanent and cannot be undone</strong>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
            <input
              type="email"
              placeholder="customer@email.com to delete"
              value={deleteEmail}
              onChange={(e) => setDeleteEmail(e.target.value)}
              className="bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500/40"
              data-testid="delete-email-input"
            />
            <input
              type="text"
              placeholder="Type DELETE (uppercase) to confirm"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              className="bg-white/5 border border-white/10 text-white placeholder-white/30 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500/40 font-mono"
              data-testid="delete-confirm-input"
            />
          </div>
          <Button
            onClick={deleteUser}
            disabled={!deleteEmail || deleteConfirm !== 'DELETE'}
            className="bg-red-600 hover:bg-red-700 disabled:bg-red-600/30 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 text-sm"
            data-testid="delete-user-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Permanently delete this user
          </Button>
        </section>

        {/* Recent users */}
        <section className="bg-white/[0.03] border border-white/5 rounded-2xl">
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
            <h2 className="text-xs uppercase tracking-wider text-white/40">Recent signups</h2>
            <span className="text-white/40 text-xs">{recent.length} shown</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/40 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-2 text-left">Email</th>
                  <th className="px-5 py-2 text-left">Name</th>
                  <th className="px-5 py-2 text-left">Verified</th>
                  <th className="px-5 py-2 text-left">Plan</th>
                  <th className="px-5 py-2 text-left">Created</th>
                  <th className="px-5 py-2 text-left">Last digest</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr key={r.user_id} className="border-t border-white/[0.03] hover:bg-white/[0.02]">
                    <td className="px-5 py-2 text-white/85">{r.email}</td>
                    <td className="px-5 py-2 text-white/60">{r.name || '—'}</td>
                    <td className="px-5 py-2">
                      {r.email_verified ? <span className="text-emerald-300 text-xs">✓</span> : <span className="text-white/30 text-xs">no</span>}
                    </td>
                    <td className="px-5 py-2 text-xs">
                      {r.is_premium ? (
                        <span className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-full px-2 py-0.5">
                          ✨ {r.plan || 'premium'}
                        </span>
                      ) : (
                        <span className="text-white/40">free</span>
                      )}
                    </td>
                    <td className="px-5 py-2 text-white/50 text-xs">{fmtDate(r.created_at)}</td>
                    <td className="px-5 py-2 text-white/50 text-xs">{fmtDate(r.last_digest_sent_at)}</td>
                  </tr>
                ))}
                {recent.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-white/35 text-xs">No users yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <p className="text-center text-white/25 text-xs pt-4">
          NeuroWave One admin · token auth only · actions are logged server-side
        </p>
      </main>
    </div>
  );
};

export default AdminPanel;
